import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../contexts/AuthContext";
import BadgeChip from "../components/BadgeChip";
import { toast } from "sonner";
import { Trophy, TrendingUp, Sparkles, ArrowRight } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
} from "recharts";

export default function MyPage() {
  const { t, tField, lang } = useI18n();
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    Promise.all([api.get("/users/me/stats"), api.get("/users/me/results")])
      .then(([s, h]) => {
        setStats(s.data);
        setHistory(h.data.items || []);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  if (authLoading || loading) {
    return <div className="py-32 text-center font-mono text-xs uppercase tracking-[0.2em] text-[#64748B]">// Loading…</div>;
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center">
        <h2 className="font-display text-4xl tracking-tight uppercase text-white mb-4">
          {t("nav.login")}
        </h2>
        <p className="text-[#94A3B8] mb-8">{t("auth.anon_note")}</p>
        <div className="flex justify-center gap-3">
          <Link
            to="/login"
            data-testid="mypage-login-link"
            className="px-6 py-3 bg-[#3B82F6] text-black font-mono text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-[#60A5FA]"
          >
            {t("nav.login")}
          </Link>
          <Link
            to="/register"
            data-testid="mypage-register-link"
            className="px-6 py-3 border border-[#1E2A3F] text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold hover:border-[#3B82F6] hover:text-[#3B82F6]"
          >
            {t("nav.register")}
          </Link>
        </div>
      </div>
    );
  }

  const remaining = Math.max(0, 3 - stats.count);
  const unlockPct = Math.min(100, Math.round((stats.count / 3) * 100));

  const trendData = (stats.trend || []).map((d, i) => ({
    idx: i + 1,
    overall: d.overall,
    name: tField(d.title),
  }));

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12 lg:py-16 space-y-12">
      {/* Header */}
      <div className="border-b border-[#1E2A3F] pb-8">
        <div className="label-mono mb-2">// Dashboard</div>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight uppercase text-white">
          {t("mypage.title")}
        </h1>
        <div
          data-testid="mypage-user-greeting"
          className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#3B82F6]">
            // {lang === "ja" ? "ようこそ" : "Welcome"}
          </span>
          <span className="font-display text-xl md:text-2xl tracking-tight text-white" style={{ fontWeight: 700 }}>
            {user.name || user.email}
          </span>
          {user.name && (
            <span className="font-mono text-xs text-[#64748B]">· {user.email}</span>
          )}
        </div>
        <p className="mt-3 text-[#94A3B8]">{t("mypage.subtitle")}</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: t("mypage.total"), value: stats.count, testId: "kpi-total" },
          { label: t("mypage.average"), value: stats.average, testId: "kpi-avg" },
          { label: t("mypage.premium_count"), value: stats.premium_count, testId: "kpi-premium" },
          { label: t("mypage.categories_covered"), value: stats.categories_covered.length, testId: "kpi-cats" },
        ].map((k) => (
          <div
            key={k.label}
            data-testid={k.testId}
            className="bg-[#0F1525] border border-[#1E2A3F] p-6 hover:border-[#3B82F6]/50 transition-colors"
          >
            <div className="label-mono mb-2">{k.label}</div>
            <div className="font-mono text-5xl text-[#3B82F6] tracking-tight tabular-nums">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Composite unlock tracker */}
      <div
        data-testid="composite-unlock-tracker"
        className={`p-8 border ${
          stats.unlocked_overall
            ? "border-[#3B82F6] bg-[#0F1525] neon-glow-soft"
            : "border-[#1E2A3F] bg-[#0F1525]"
        }`}
      >
        <div className="flex items-center gap-3 mb-4">
          <Trophy className={`h-5 w-5 ${stats.unlocked_overall ? "text-[#3B82F6]" : "text-[#94A3B8]"}`} strokeWidth={1.5} />
          <h2 className="font-display text-2xl tracking-tight uppercase text-white">
            {stats.unlocked_overall ? t("mypage.unlocked") : t("mypage.unlock_3_title")}
          </h2>
        </div>
        {!stats.unlocked_overall && (
          <>
            <p className="text-sm mb-4 text-[#94A3B8]">
              {t("mypage.unlock_3_desc", { n: remaining })}
            </p>
            <div className="h-1.5 bg-[#1E2A3F] overflow-hidden">
              <div
                className="h-full bg-[#3B82F6] progress-fill"
                style={{ width: `${unlockPct}%`, boxShadow: "0 0 8px rgba(59,130,246,0.6)" }}
              />
            </div>
            <div className="mt-2 font-mono text-[11px] text-[#64748B]">
              {stats.count} / 3
            </div>
          </>
        )}
      </div>

      {/* Trend chart + Badges */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#0F1525] border border-[#1E2A3F] p-6 md:p-8">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-[#3B82F6]" strokeWidth={1.5} />
            <div className="label-mono">{t("mypage.trend_title")}</div>
          </div>
          {trendData.length === 0 ? (
            <div className="py-12 text-center text-sm text-[#64748B]">{t("mypage.no_history")}</div>
          ) : (
            <div className="w-full h-[280px]" data-testid="trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid stroke="#1E2A3F" strokeDasharray="3 3" />
                  <XAxis dataKey="idx" stroke="#64748B" fontSize={11} />
                  <YAxis domain={[0, 100]} stroke="#64748B" fontSize={11} />
                  <RTooltip
                    contentStyle={{ borderRadius: 0, border: "1px solid #3B82F6", background: "#0F1525", color: "#fff", fontSize: 12 }}
                    formatter={(v) => [v, "Overall"]}
                    labelFormatter={(l) => `#${l}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="overall"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "#0A0E1A", stroke: "#3B82F6", strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: "#3B82F6", stroke: "#3B82F6" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="bg-[#0F1525] border border-[#1E2A3F] p-6 md:p-8">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-4 w-4 text-[#3B82F6]" strokeWidth={1.5} />
            <div className="label-mono">{t("mypage.badges_title")}</div>
          </div>
          {stats.badges.length === 0 ? (
            <div className="py-6 text-sm text-[#64748B]">—</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {stats.badges.map((b) => (
                <BadgeChip key={b} badge={b} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* History list */}
      <div>
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="label-mono mb-1">// History</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight uppercase text-white">
              {t("mypage.history_title")}
            </h2>
          </div>
          <Link
            to="/catalog"
            data-testid="mypage-take-new-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#3B82F6] hover:underline"
          >
            {t("result.back_to_catalog")}
            <ArrowRight className="h-3 w-3" strokeWidth={2} />
          </Link>
        </div>
        {history.length === 0 ? (
          <div className="py-20 text-center border border-dashed border-[#1E2A3F] bg-[#0F1525]">
            <div className="text-[#94A3B8] mb-4">{t("mypage.no_history")}</div>
            <Link
              to="/catalog"
              data-testid="mypage-empty-cta"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#3B82F6] text-black font-mono text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-[#60A5FA]"
            >
              {t("hero.cta_primary")}
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {history.map((h) => {
              const isIQ = h.diagnostic_id === "iq";
              const iqScore = isIQ ? Math.round(70 + (h.overall * 1.1)) : null;
              return (
              <Link
                key={h.id}
                to={`/result/${h.id}`}
                data-testid={`history-item-${h.id}`}
                className="bg-[#0F1525] border border-[#1E2A3F] p-6 card-hoverable group"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
                    {tField(h.diagnostic_title)}
                  </div>
                  {h.is_premium && (
                    <span className="px-2 py-0.5 bg-[#3B82F6] text-black font-mono text-[10px] uppercase tracking-[0.2em] font-bold">
                      Premium
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  {isIQ ? (
                    <>
                      <span className="font-mono text-xs text-[#64748B]">IQ</span>
                      <span className="font-mono text-5xl text-[#3B82F6] tracking-tight tabular-nums">
                        {iqScore}
                      </span>
                      <span className="font-mono text-xs text-[#64748B]">/ 180</span>
                    </>
                  ) : (
                    <>
                      <span className="font-mono text-5xl text-[#3B82F6] tracking-tight tabular-nums">
                        {h.overall}
                      </span>
                      <span className="font-mono text-xs text-[#64748B]">/ 100</span>
                    </>
                  )}
                </div>
                <div className="text-sm font-medium text-white">{tField(h.type_label)}</div>
                <div className="mt-4 pt-4 border-t border-[#1E2A3F] flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
                  <span>{new Date(h.created_at).toLocaleDateString(lang)}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-[#3B82F6] transition-transform group-hover:translate-x-1" strokeWidth={2} />
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
